import React from 'react';
import styled, { css } from 'styled-components';

import CalloutBadge from '@rover/components/src/callouts/CalloutBadge';
import { useEnableSchema } from '@rover/components/src/microdata';
import { Box, Flex } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import { TrainerFlair } from '@rover/react-lib/src/pages/training/components/TrainerFlair';
import type { TrainerSearchResultFlair } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResultFlair';
import type { ProfileStatusBadge } from '@rover/types/src/ProfileStatusBadge';
import type { SitterBadge } from '@rover/types/src/SitterBadge';

import { SEARCH_RESULT_CARD_Z_INDEX } from '../SearchResultCard.constants';
import SitterBadges from '../SitterBadges';

export type Props = {
  badgeData: SitterBadge[];
  displayExpanded: boolean;
  name: string;
  handleBadgeChange: (arg0: boolean) => void;
  index?: number;
  isBadgeOpen: boolean;
  profileStatusBadgeData?: ProfileStatusBadge;
  rank?: number | null;
  showRank?: boolean;
  flair?: TrainerSearchResultFlair;
};

const expandedFontStyles = css`
  font-size: ${({ theme }) => theme.heading['400'].fontSize};
  font-weight: ${({ theme }) => theme.heading['300'].fontWeight};
  color: ${({ theme }) => theme.colors.text.primary};
  :hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const reducedFontStyles = css`
  font-size: ${({ theme }) => theme.heading['200'].fontSize};
  font-weight: ${({ theme }) => theme.heading['200'].fontWeight};
`;

type NameLinkProps = {
  displayExpanded: boolean;
};

const StyledSitterNameHeading = styled.h3<NameLinkProps>`
  ${expandedFontStyles}

  ${MQ.LG.toString()} {
    ${(props) => (props.displayExpanded ? '' : reducedFontStyles)}
  }

  margin-top: 0;
  margin-bottom: 0;
  margin-right: ${({ theme }) => theme.space['1x']};
  overflow-wrap: break-word;

  ${MQ.XS_DOWN.toString()} {
    ${reducedFontStyles}
  }
`;

const NameRow: React.FC<Props> = ({
  badgeData,
  displayExpanded,
  name,
  handleBadgeChange,
  index,
  isBadgeOpen,
  profileStatusBadgeData,
  rank,
  showRank,
  flair,
}) => {
  const enableSchema = useEnableSchema();

  return (
    // @ts-expect-error
    <Flex flexWrap="wrap" mt="-4px" mb="1x" alignItems="center">
      <Flex mt="1x" alignItems="center" flexWrap="wrap">
        <StyledSitterNameHeading displayExpanded={displayExpanded} aria-label={name}>
          {showRank && rank != null && `${rank}. `}
          <span style={{ marginRight: '16px' }} {...(enableSchema ? { itemProp: 'name' } : {})}>
            {name}
          </span>
          <Flex
            position="relative"
            display="inline-flex"
            mt="1x"
            zIndex={
              isBadgeOpen
                ? SEARCH_RESULT_CARD_Z_INDEX.BADGE_OPEN.toString()
                : SEARCH_RESULT_CARD_Z_INDEX.BADGE_CLOSED.toString()
            }
          >
            <SitterBadges
              badgeData={badgeData}
              displayExpanded={displayExpanded}
              handleBadgeChange={handleBadgeChange}
              index={index}
              badgeProps={enableSchema ? { itemProp: 'hasCredential' } : {}}
            />
          </Flex>
          {profileStatusBadgeData && profileStatusBadgeData.slug && (
            <Box
              sx={{ whiteSpace: 'nowrap' }}
              display="inline"
              ml="1x"
              position="relative"
              top={['0', '-7px', '-7px']}
            >
              {profileStatusBadgeData.slug === 'star-sitter' && (
                <CalloutBadge
                  value={profileStatusBadgeData.text}
                  calloutBadgeStyle={CalloutBadge.Styles.STAR_SITTER}
                  {...(enableSchema ? { itemProp: 'award' } : {})}
                />
              )}
              {profileStatusBadgeData.slug === 'local-facility' && (
                <CalloutBadge
                  value={profileStatusBadgeData.text}
                  calloutBadgeStyle={CalloutBadge.Styles.LOCAL_FACILITY}
                />
              )}
            </Box>
          )}
        </StyledSitterNameHeading>
        {flair && <TrainerFlair slug={flair.slug} displayText={flair.displayText} />}
      </Flex>
    </Flex>
  );
};

export default NameRow;
